import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { FiArrowRight, FiMonitor, FiX } from 'react-icons/fi';
import styles from './OnlineProgramAnnouncement.module.css';

const OnlineProgramAnnouncement = () => {
  const [isOpen, setIsOpen] = useState(false);
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const isEligiblePage = location.pathname === '/' || /^\/courses\/[^/]+\/?$/.test(location.pathname);
    if (!isEligiblePage) return undefined;

    const timer = window.setTimeout(() => {
      setIsOpen(true);
    }, 100);

    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusableElements = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus({ preventScroll: true });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      if (
        previouslyFocused instanceof HTMLElement &&
        previouslyFocused !== document.body &&
        previouslyFocused.isConnected
      ) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
        >
          <motion.section
            ref={dialogRef}
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="online-announcement-title"
            aria-describedby="online-announcement-description"
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.2, 0.75, 0.25, 1] }}
          >
            <button
              ref={closeButtonRef}
              className={styles.closeButton}
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close online learning announcement"
            >
              <FiX aria-hidden="true" />
            </button>
            <span className={styles.icon} aria-hidden="true"><FiMonitor /></span>
            <p className={styles.eyebrow}>Online programs now available</p>
            <h2 id="online-announcement-title">It’s at your fingertips!</h2>
            <p className={styles.message} id="online-announcement-description">
              You can now continue your course online. Just you and your mentor, live and 1-on-1, from wherever you are.
            </p>
            <Link
              className={styles.cta}
              to="/online-learning"
              onClick={() => setIsOpen(false)}
            >
              Explore online programs <FiArrowRight aria-hidden="true" />
            </Link>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OnlineProgramAnnouncement;